"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { useEffect, Suspense } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import { parseConsent } from "@/components/site/cookie-consent/utils";
import { initPostHog, consumeInitialPageview } from "@/lib/posthog";

function PostHogPageView() {
  const ph = usePostHog();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (ph && ph.__loaded) {
      const search = searchParams?.toString();
      const url = `${window.location.origin}${pathname}${search ? `?${search}` : ""}`;
      if (consumeInitialPageview(url)) return;
      ph.capture("$pageview", { $current_url: url });
    }
  }, [ph, pathname, searchParams]);

  return null;
}

interface PostHogProviderProps {
  children: React.ReactNode;
}

export function PostHogProvider({ children }: PostHogProviderProps) {
  useEffect(() => {
    // Strict opt-in: PostHog only initializes once analytics consent has
    // been granted. This mount check covers repeat visitors (cookie already
    // present); first-time grants — a banner accept, or CookieYes's implied
    // grant in non-targeted regions — are picked up by ConsentSync, which
    // boots the SDK via applyPostHogConsent.
    const { consented, prefs } = parseConsent();
    if (consented && (prefs.analytics || prefs.performance)) {
      initPostHog();
    }
  }, []);

  return (
    <PHProvider client={posthog}>
      <Suspense fallback={null}>
        <PostHogPageView />
      </Suspense>
      {children}
    </PHProvider>
  );
}
